import type { Feature, FeatureCollection, Polygon } from "geojson";
import type { MapTool } from "../../../application/map/tools/types";
import type { ToolDeps } from "./types";
import { drawBoundaryUseCase } from "../../../application/map/useCases/drawBoundaryUseCase";
import { ensureRingsClosed } from "../../helpers/JobMap/editHelpers";
import type { FeatureId } from "../engine/useMapDrawEngine";

export function makeDrawBoundaryTool(deps: ToolDeps): MapTool {
	let jobId: string | null = null;
	let freehandActive = false;

	function onFinish(_id: FeatureId, _ctx: { mode: string; action: string }) {
		if (!jobId) return;

		const snapshot = deps.drawEngine.getSnapshot();
		const drawn = snapshot.find((f) => f.geometry.type === "Polygon");
		if (!drawn) return;

		const drawnFeature: Feature<Polygon> = {
			type: "Feature",
			properties: {},
			geometry: {
				type: "Polygon",
				coordinates: ensureRingsClosed((drawn.geometry as Polygon).coordinates),
			},
		};

		const existingFC: FeatureCollection = deps.getBoundaryFC(jobId) ?? {
			type: "FeatureCollection",
			features: [],
		};

		const result = drawBoundaryUseCase(
			drawnFeature,
			existingFC,
			(fc) => deps.applyBoundary(jobId!, fc),
			(fc) => deps.applyBoundary(jobId!, fc),
		);

		if (!result.ok) {
			deps.showError(result.error);
			return;
		}

		deps.undoStack.push(result.data.command);
		deps.drawEngine.clear();
	}

	return {
		id: "draw-boundary",
		get label() {
			return freehandActive ? "Boundary (Freehand)" : "Draw Boundary";
		},
		cursor: "crosshair",
		onToggleMode() {
			if (!freehandActive) {
				freehandActive = true;
				deps.drawEngine.clear();
				deps.drawEngine.setMode("freehand");
				deps.drawEngine.setFinishHandler(onFinish);
				return true;
			}
			return false; // → deactivate
		},
		onActivate() {
			jobId = deps.getSelectedJobId();
			if (!jobId) {
				deps.showError("No job selected");
				deps.deactivateTool();
				return;
			}
			deps.drawEngine.setMode("polygon");
			deps.drawEngine.setFinishHandler(onFinish);
		},
		onDeactivate() {
			freehandActive = false;
			deps.drawEngine.setFinishHandler(null);
			deps.drawEngine.clear();
			deps.drawEngine.setMode("static");
			jobId = null;
		},
	};
}
